import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { FMProvider, useFM } from '@/context/FMContext';
import GymSelector from '@/components/fm/GymSelector';
import EscalationFAB from '@/components/fm/EscalationFAB';
import OfflineSyncIndicator from '@/components/fm/OfflineSyncIndicator';
import { getDB } from '@/lib/offlineDb';

/**
 * FMLayout - Mobile-first layout shell for FM pages.
 * Completely separate from the existing desktop Layout.js.
 * Features: sticky header with gym selector, offline indicator, no sidebar, escalation FAB.
 */
export default function FMLayout({ children }) {
  return (
    <FMProvider>
      <FMLayoutInner>{children}</FMLayoutInner>
    </FMProvider>
  );
}

/**
 * Inner layout component that can access FMContext (offline sync state).
 */
function FMLayoutInner({ children }) {
  const navigate = useNavigate();
  const { isOnline, pendingSyncCount, syncing, lastSyncTime } = useFM();

  const handleLogout = async () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('fm_selected_gym');
    // Clear offline data on logout
    try {
      const db = await getDB();
      const tx = db.transaction(['syncQueue', 'cache'], 'readwrite');
      await tx.objectStore('syncQueue').clear();
      await tx.objectStore('cache').clear();
      await tx.done;
    } catch {
      // IndexedDB may not be available
    }
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Sticky top bar */}
      <header className="sticky top-0 z-40 bg-white border-b shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between max-w-lg lg:max-w-4xl mx-auto">
            <h1 className="font-bold text-lg text-slate-800">WTF FM</h1>
            <div className="flex items-center gap-2">
              <GymSelector />
              {/* Pending sync count badge next to gym selector */}
              {isOnline && pendingSyncCount > 0 && !syncing && (
                <span className="bg-amber-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                  {pendingSyncCount}
                </span>
              )}
              <button
                onClick={handleLogout}
                className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
                aria-label="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Offline/sync indicator below header */}
        <OfflineSyncIndicator
          isOnline={isOnline}
          syncing={syncing}
          pendingSyncCount={pendingSyncCount}
          lastSyncTime={lastSyncTime}
        />
      </header>

      {/* Main content */}
      <main className="px-4 py-4 max-w-lg lg:max-w-4xl mx-auto">
        {children}
      </main>

      {/* Floating Escalation Button */}
      <EscalationFAB />
    </div>
  );
}
